import React, { useState, useEffect, useRef, useCallback } from 'react';
import PedigreeIFrame from '../components/pedigree-iframe';
import axios from 'axios';
import './pedigree-viewer-page.css';

const isEmbedded = (() => {
    try {
        return window.self !== window.top;
    } catch (e) {
        // Cross-origin access to window.top throws, which also means we're framed
        return true;
    }
})();

// TODO: restrict to known parent origins before production
const postToParent = (message) => {
    if (!isEmbedded) return;
    window.parent.postMessage(message, '*');
};

// The save click happens inside the clinician portal iframe, so this document usually isn't focused and
// navigator.clipboard rejects. Fall back to execCommand, which works using the click's user activation
// (it propagates from the child iframe to this page).
const copyToClipboard = async (text) => {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (e) {
        // fall through to the execCommand fallback
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let copied = false;
    try {
        copied = document.execCommand('copy');
    } catch (e) {
        copied = false;
    }
    document.body.removeChild(textarea);
    return copied;
};

const PedigreeViewerPage = () => {
    const clinicianPortalDomain = process.env.REACT_APP_CLINICIAN_PORTAL_DOMAIN;
    const rootApiURL = process.env.REACT_APP_ROOT_API_URL;

    const [jsonContent, setJsonContent] = useState(`{}`);
    const [iframeURL] = useState(`${clinicianPortalDomain}/pedigree-viewer/`);
    const [iframeKey] = useState(Date.now());
    const [orgIdentifier, setOrgIdentifier] = useState("");
    const [errorMessages, setErrorMessages] = useState([]);
    // The sidebar is an overlay drawer, hidden by default. Embedded: the toggle disappears once the host sends data.
    const [showSidebar, setShowSidebar] = useState(false);
    const [parentDataReceived, setParentDataReceived] = useState(false);
    const canToggleSidebar = !isEmbedded || !parentDataReceived;
    // status: idle | loading | done | error. Every criterion returned by the API is one that was met.
    const [risk, setRisk] = useState({ status: 'idle', criteria: [], error: null });
    const [riskExpanded, setRiskExpanded] = useState(false);
    const [toast, setToast] = useState(null);
    // Only true once the portal confirms it answers pedigree-viewer-request-json-save; older portals
    // never send the confirmation and keep showing their own save button instead.
    const [saveSupported, setSaveSupported] = useState(false);
    // Load options the clinician portal reads from the pedigree JSON (run_risk, run_pdf_generation, enable_umls_search)
    const [runRiskAutomatically, setRunRiskAutomatically] = useState(false);
    const [runPDFGenerationAutomatically, setRunPDFGenerationAutomatically] = useState(false);
    const [enableUMLSSearch, setEnableUMLSSearch] = useState(true);
    const [customDiseaseList, setCustomDiseaseList] = useState(`[]`);
    // Outputs the portal sends back when run_risk / run_pdf_generation are on
    const [riskData, setRiskData] = useState(null);
    const [base64Data, setBase64Data] = useState(null);
    const [riskDataPlaceholder, setRiskDataPlaceholder] = useState("Risk data will appear here after generating...");
    const [base64DataPlaceholder, setBase64DataPlaceholder] = useState("Base64 data will appear here after generating...");
    const [copiedField, setCopiedField] = useState(null);

    const innerIframeLoaded = useRef(false);
    const pendingLoad = useRef(null);
    const riskRequestId = useRef(0);
    const toastTimer = useRef(null);
    const saveRequestTimer = useRef(null);
    const jsonRequests = useRef(new Map());
    const nextJsonRequestId = useRef(0);
    // The clinician portal requests this after navigating (request-pedigree-json)
    const loadedPedigreeJsonRef = useRef(null);

    const showToast = useCallback((message, type) => {
        clearTimeout(toastTimer.current);
        setToast({ message, type });
        toastTimer.current = setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => () => {
        clearTimeout(toastTimer.current);
        clearTimeout(saveRequestTimer.current);
        jsonRequests.current.forEach(({ timer }) => clearTimeout(timer));
        jsonRequests.current.clear();
    }, []);

    const requestCurrentJson = useCallback((requestId) => {
        const replyError = (message) => postToParent({
            action: "pedigree-viewer-json-error", request_id: requestId, message,
        });
        const iframe = document.getElementById('pedigree-iframe');
        if (!loadedPedigreeJsonRef.current || !iframe || !iframe.contentWindow) {
            replyError("No pedigree is loaded.");
            return;
        }
        const internalId = String(++nextJsonRequestId.current);
        const timer = setTimeout(() => {
            jsonRequests.current.delete(internalId);
            replyError("The clinician portal did not respond to the pedigree JSON request.");
        }, 5000);
        jsonRequests.current.set(internalId, { requestId, timer });
        iframe.contentWindow.postMessage({
            action: "pedigree-viewer-get-json", request_id: internalId,
        }, clinicianPortalDomain);
    }, [clinicianPortalDomain]);

    const handleJsonDataSave = useCallback(async (jsonData) => {
        clearTimeout(saveRequestTimer.current);
        saveRequestTimer.current = null;
        let parsed = jsonData;
        let text = jsonData;
        if (typeof jsonData === 'string') {
            try {
                parsed = JSON.parse(jsonData);
            } catch (e) {
                parsed = null;
            }
        } else {
            text = JSON.stringify(jsonData);
        }
        if (parsed && typeof parsed === 'object') {
            text = JSON.stringify(parsed, null, 2);
            // The saved JSON has the load flags stripped; keep them in case the portal requests the JSON again
            const previous = loadedPedigreeJsonRef.current || {};
            const flags = {};
            ['run_risk', 'run_pdf_generation', 'enable_umls_search', 'custom_disease_list'].forEach((key) => {
                if (key in previous && !(key in parsed)) flags[key] = previous[key];
            });
            loadedPedigreeJsonRef.current = { ...parsed, ...flags };
            setJsonContent(text);
        }

        postToParent({ action: "pedigree-viewer-json-data-save", json_data: parsed || jsonData });

        const copied = await copyToClipboard(text);
        if (copied) {
            showToast("Pedigree JSON copied to clipboard", "success");
        } else {
            showToast("Couldn't copy to the clipboard. The JSON has been put in the sidebar instead.", "error");
        }
    }, [showToast]);

    // Risk results re-run by the clinician portal (e.g. after the user edits the pedigree)
    const applyRiskCriteria = useCallback((riskCriteria) => {
        // Discard any viewer-side check still in flight so it can't overwrite these newer results
        riskRequestId.current++;
        const criteria = Array.isArray(riskCriteria) ? riskCriteria : [];
        setRisk({ status: 'done', criteria, error: null });
        if (criteria.length === 0) setRiskExpanded(false);
        postToParent({ action: "pedigree-viewer-risk", at_risk: criteria.length > 0, criteria });
    }, []);

    // add event listener for messages from the iframe (clinician portal)
    useEffect(() => {
        document.title = "FamGenix | Pedigree Viewer";
        const handleClinicianMessage = (event) => {
            // if it's not from the clinician portal domain, ignore
            const iframe = document.getElementById('pedigree-iframe');
            if (event.origin !== clinicianPortalDomain || !iframe || event.source !== iframe.contentWindow) return;
            let data;
            try {
                data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            } catch (e) {
                return;
            }
            if (data && (data.action === "pedigree-viewer-json" || data.action === "pedigree-viewer-json-error")) {
                const request = jsonRequests.current.get(data.request_id);
                if (!request) return;
                clearTimeout(request.timer);
                jsonRequests.current.delete(data.request_id);
                try {
                    if (data.action === "pedigree-viewer-json-error") {
                        throw new Error(data.message || "The clinician portal could not export the pedigree.");
                    }
                    const json = typeof data.json_data === 'string' ? JSON.parse(data.json_data) : data.json_data;
                    if (!json || typeof json !== 'object' || Array.isArray(json)) {
                        throw new Error("The clinician portal returned invalid pedigree JSON.");
                    }
                    postToParent({ action: "pedigree-viewer-json", request_id: request.requestId, family_json: json });
                } catch (e) {
                    postToParent({ action: "pedigree-viewer-json-error", request_id: request.requestId, message: e.message });
                }
                return;
            }
            if (data && data.action === "request-pedigree-json") {
                if (event.source && loadedPedigreeJsonRef.current) {
                    event.source.postMessage({
                        action: "pedigree-viewer-json-data",
                        json_data: loadedPedigreeJsonRef.current,
                        // This page renders its own save button, so the portal hides its built-in one
                        host_save_button: true,
                    }, clinicianPortalDomain);
                }
                return;
            }
            if (data && data.action === "pedigree-viewer-save-supported") {
                setSaveSupported(true);
                return;
            }
            if (data && data.action === "pedigree-viewer-json-data-save") {
                handleJsonDataSave(data.json_data);
                return;
            }
            if (data && data.action === "pedigree-viewer-risk-criteria") {
                applyRiskCriteria(data.risk_criteria);
                return;
            }
            if (data && data.action === "pedigree-viewer-risk-data") {
                setRiskData(JSON.stringify(data.riskData));
                sessionStorage.setItem("pedigreeRiskData", JSON.stringify(data.riskData));
                postToParent({ action: "pedigree-viewer-risk-data", risk_data: data.riskData });
                return;
            }
            if (data && data.action === "pedigree-viewer-print") {
                setBase64Data(data.base64Data);
                sessionStorage.setItem("pedigreePrintData", JSON.stringify(data.base64Data));
                postToParent({ action: "pedigree-viewer-print", base64_data: data.base64Data });
            }
        };
        window.addEventListener("message", handleClinicianMessage);
        return () => window.removeEventListener("message", handleClinicianMessage);
    }, [clinicianPortalDomain, handleJsonDataSave, applyRiskCriteria]);

    const checkRisk = useCallback(async (json, accessToken) => {
        const requestId = ++riskRequestId.current;
        setRisk({ status: 'loading', criteria: [], error: null });
        setRiskExpanded(false);
        try {
            const response = await axios.post(
                `${rootApiURL}/risk/check_risk/`,
                { imported_pedigree_json_data: json, is_to_be_saved: false },
                { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` } }
            );
            // Ignore responses for an older pedigree if a newer one has been loaded
            if (requestId !== riskRequestId.current) return;
            const criteria = Array.isArray(response.data && response.data.result) ? response.data.result : [];
            setRisk({ status: 'done', criteria, error: null });
            postToParent({ action: "pedigree-viewer-risk", at_risk: criteria.length > 0, criteria });
        } catch (e) {
            if (requestId !== riskRequestId.current) return;
            console.log(e.message);
            setRisk({ status: 'error', criteria: [], error: e.message });
            postToParent({ action: "pedigree-viewer-risk", at_risk: null, error: e.message });
        }
    }, [rootApiURL]);

    // Returns null on success, or an error message string on failure.
    // options: { runRisk, runPdfGeneration, enableUmlsSearch, customDiseaseList }
    const loadPedigree = useCallback(async (familyJson, orgKey, options = {}) => {
        try {
            const json = typeof familyJson === 'string' ? JSON.parse(familyJson) : familyJson;
            if (!json || typeof json !== 'object' || Array.isArray(json)) {
                throw new Error("family_json must be a JSON object");
            }
            let diseaseList = options.customDiseaseList;
            if (typeof diseaseList === 'string') {
                diseaseList = diseaseList.trim() ? JSON.parse(diseaseList) : [];
            }

            // The portal reads these flags from the JSON it requests back (request-pedigree-json)
            const portalJson = {
                ...json,
                run_risk: options.runRisk === true,
                run_pdf_generation: options.runPdfGeneration === true,
                enable_umls_search: options.enableUmlsSearch !== false,
            };
            if (Array.isArray(diseaseList) && diseaseList.length > 0) {
                portalJson.custom_disease_list = diseaseList;
            }
            loadedPedigreeJsonRef.current = portalJson;
            setSaveSupported(false);
            setRiskData(null);
            setBase64Data(null);
            setRiskDataPlaceholder(portalJson.run_risk
                ? "Generating risk data..."
                : "Risk data will appear here after generating...");
            setBase64DataPlaceholder(portalJson.run_pdf_generation
                ? "Generating base64 data..."
                : "Base64 data will appear here after generating...");
            const iframe = document.getElementById('pedigree-iframe');
            if (!iframe || !iframe.contentWindow) {
                throw new Error("Pedigree iframe is not available");
            }

            const response = await axios({
                method: "POST",
                url: `${rootApiURL}/organization/get_access_token/`,
                headers: { 'Content-Type': 'application/json' },
                data: { org_identifier: orgKey },
                validateStatus: function (status) {
                    // Make sure 304 status is not treated as error
                    if (status === 304) return true;
                    return status >= 200 && status < 300;
                }
            });

            if (response.status !== 200) {
                throw new Error(`Access token request returned status ${response.status}`);
            }

            const payload = {
                url: `${clinicianPortalDomain}/pedigree/viewer`,
                action: "pedigree-viewer",
                json_data: portalJson,
                payload: {
                    token: {
                        accessToken: response.data.accessToken
                    },
                    account: {
                        access_type: "read-only",
                    },
                    profile: {},
                    organization: {
                        identifier: orgKey
                    },
                }
            };

            iframe.contentWindow.postMessage(payload, String(clinicianPortalDomain));
            setErrorMessages([]);
            checkRisk(json, response.data.accessToken);
            return null;
        } catch (e) {
            console.log(e.message);
            setErrorMessages([e.message]);
            return e.message;
        }
    }, [rootApiURL, clinicianPortalDomain, checkRisk]);

    // Pretty-print a JSON textarea in place; leaves it untouched if it doesn't parse
    const formatJsonField = (text, setText) => {
        try {
            setText(JSON.stringify(JSON.parse(text), null, 2));
        } catch (e) {
            // loadPedigree reports the parse error
        }
    };

    const handleManualLoad = async () => {
        formatJsonField(jsonContent, setJsonContent);
        formatJsonField(customDiseaseList, setCustomDiseaseList);
        const error = await loadPedigree(jsonContent, orgIdentifier.trim(), {
            runRisk: runRiskAutomatically,
            runPdfGeneration: runPDFGenerationAutomatically,
            enableUmlsSearch: enableUMLSSearch,
            customDiseaseList,
        });
        if (!error) setShowSidebar(false);
    };

    const copyOutput = async (text, field) => {
        if (!text) return;
        const copied = await copyToClipboard(text);
        if (copied) {
            setCopiedField(field);
            setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 2000);
        } else {
            showToast("Couldn't copy to the clipboard.", "error");
        }
    };

    // Ask the clinician portal for the current (possibly edited) pedigree JSON.
    // It replies with pedigree-viewer-json-data-save, which handleJsonDataSave copies to the clipboard.
    const requestJsonSave = () => {
        const iframe = document.getElementById('pedigree-iframe');
        if (!iframe || !iframe.contentWindow) {
            showToast("The pedigree isn't available to save yet.", "error");
            return;
        }
        clearTimeout(saveRequestTimer.current);
        saveRequestTimer.current = setTimeout(() => {
            saveRequestTimer.current = null;
            showToast("The pedigree viewer didn't respond. Try again once the pedigree has finished loading.", "error");
        }, 5000);
        iframe.contentWindow.postMessage(
            JSON.stringify({ action: "pedigree-viewer-request-json-save" }),
            String(clinicianPortalDomain)
        );
    };

    const runEmbeddedLoad = useCallback(async ({ familyJson, orgKey, options }) => {
        const error = await loadPedigree(familyJson, orgKey, options);
        if (error) {
            postToParent({ action: "pedigree-viewer-error", message: error });
        } else {
            postToParent({ action: "pedigree-viewer-loaded" });
        }
    }, [loadPedigree]);

    // Embedded mode: accept family_json + org_key from the parent window
    useEffect(() => {
        if (!isEmbedded) return;

        const handleParentMessage = (event) => {
            if (event.source !== window.parent) return;
            let data = event.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch (e) {
                    return;
                }
            }
            if (data && data.action === "pedigree-viewer-get-json") {
                requestCurrentJson(data.request_id);
                return;
            }
            if (!data || data.action !== "load-pedigree") return;

            const orgKey = data.org_key;
            const familyJson = data.family_json;
            if (typeof orgKey !== 'string' || !orgKey.trim()) {
                postToParent({ action: "pedigree-viewer-error", message: "org_key is required" });
                return;
            }
            if (familyJson === undefined || familyJson === null || familyJson === '') {
                postToParent({ action: "pedigree-viewer-error", message: "family_json is required" });
                return;
            }

            setParentDataReceived(true);
            setShowSidebar(false);

            const request = {
                familyJson,
                orgKey: orgKey.trim(),
                options: {
                    runRisk: data.run_risk === true,
                    runPdfGeneration: data.run_pdf_generation === true,
                    enableUmlsSearch: data.enable_umls_search !== false,
                    customDiseaseList: data.custom_disease_list,
                },
            };
            if (innerIframeLoaded.current) {
                runEmbeddedLoad(request);
            } else {
                // Clinician portal iframe not ready yet; send once it loads
                pendingLoad.current = request;
            }
        };

        window.addEventListener("message", handleParentMessage);
        postToParent({ action: "pedigree-viewer-ready" });
        return () => window.removeEventListener("message", handleParentMessage);
    }, [runEmbeddedLoad, requestCurrentJson]);

    const handleInnerIframeLoad = () => {
        innerIframeLoaded.current = true;
        if (pendingLoad.current) {
            const request = pendingLoad.current;
            pendingLoad.current = null;
            runEmbeddedLoad(request);
        }
    };

    const atRisk = risk.criteria.length > 0;

    return (
        <div className={`pedigree-viewer-container${isEmbedded ? ' embedded' : ''}`}>
            <PedigreeIFrame
                iframeKey={iframeKey}
                iframeURL={iframeURL}
                onLoad={handleInnerIframeLoad}
                width="100%"
            />

            {risk.status !== 'idle' && (
                <div
                    className={`risk-panel ${risk.status === 'done' ? (atRisk ? 'at-risk' : 'no-risk') : risk.status}`}
                    role="status"
                >
                    {risk.status === 'loading' && <div className="risk-title">Checking risk criteria...</div>}
                    {risk.status === 'error' && (
                        <div className="risk-title" title={risk.error}>Risk criteria check unavailable</div>
                    )}
                    {risk.status === 'done' && !atRisk && (
                        <div className="risk-title">No risk criteria met</div>
                    )}
                    {risk.status === 'done' && atRisk && (
                        <>
                            <button
                                type="button"
                                className="risk-title risk-toggle"
                                aria-expanded={riskExpanded}
                                onClick={() => setRiskExpanded(!riskExpanded)}
                            >
                                At risk: meets {risk.criteria.length} referral criteri{risk.criteria.length === 1 ? 'on' : 'a'}
                                <span className="risk-toggle-icon">{riskExpanded ? '\u25B2' : '\u25BC'}</span>
                            </button>
                            {riskExpanded && (
                                <ul className="risk-list">
                                    {risk.criteria.map((criterion, index) => (
                                        <li
                                            key={criterion.criteria_id || index}
                                            title={criterion.clinician_risk_factors_tooltip || undefined}
                                        >
                                            {criterion.clinician_risk_factors_main_text || criterion.app_risk_factors}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            )}

            {toast && (
                <div className={`toast-message ${toast.type}`} role="status">
                    {toast.message}
                </div>
            )}

            {!showSidebar && (canToggleSidebar || saveSupported) && (
                <div className="bottom-left-actions">
                    {canToggleSidebar && (
                        <button
                            type="button"
                            className="action-button"
                            onClick={() => setShowSidebar(true)}
                        >
                            Enter pedigree data
                        </button>
                    )}
                    {saveSupported && (
                        <button
                            type="button"
                            className="action-button secondary"
                            onClick={requestJsonSave}
                        >
                            Save pedigree JSON
                        </button>
                    )}
                </div>
            )}

            {/* Slide-over drawer - manual org identifier + JSON entry */}
            {showSidebar && (
                <div className="sidebar-drawer">
                    <div className="drawer-header">
                        <span className="drawer-title">Pedigree data</span>
                        {canToggleSidebar && (
                            <button
                                type="button"
                                className="sidebar-hide-button"
                                aria-label="Close sidebar"
                                onClick={() => setShowSidebar(false)}
                            >
                                &times;
                            </button>
                        )}
                    </div>
                    <div className="drawer-body">
                        <label className="input-label" htmlFor="org-identifier">
                            Organization Identifier
                        </label>
                        <input
                            id="org-identifier"
                            className="org-input"
                            type="text"
                            value={orgIdentifier}
                            onChange={(e) => setOrgIdentifier(e.target.value)}
                            placeholder="Enter organization identifier..."
                        />

                        <div className="option-group">
                            <label className="option-label" htmlFor="run-risk">
                                <input
                                    id="run-risk"
                                    type="checkbox"
                                    checked={runRiskAutomatically}
                                    onChange={(e) => setRunRiskAutomatically(e.target.checked)}
                                />
                                Run Risk Automatically
                            </label>
                            <label className="option-label" htmlFor="run-pdf-generation">
                                <input
                                    id="run-pdf-generation"
                                    type="checkbox"
                                    checked={runPDFGenerationAutomatically}
                                    onChange={(e) => setRunPDFGenerationAutomatically(e.target.checked)}
                                />
                                Run PDF Generation Automatically
                            </label>
                            <label className="option-label" htmlFor="enable-umls-search">
                                <input
                                    id="enable-umls-search"
                                    type="checkbox"
                                    checked={enableUMLSSearch}
                                    onChange={(e) => setEnableUMLSSearch(e.target.checked)}
                                />
                                Enable UMLS Search
                            </label>
                        </div>

                        <label className="input-label" htmlFor="json-editor">
                            Pedigree JSON Data
                        </label>
                        <textarea
                            id="json-editor"
                            className="json-textarea"
                            value={jsonContent}
                            onChange={(e) => setJsonContent(e.target.value)}
                            placeholder="Enter your pedigree JSON data here..."
                        />

                        <label className="input-label" htmlFor="custom-disease-list-editor">
                            Custom Disease List JSON Data
                        </label>
                        <textarea
                            id="custom-disease-list-editor"
                            className="json-textarea small"
                            value={customDiseaseList}
                            onChange={(e) => setCustomDiseaseList(e.target.value)}
                            placeholder="Enter your custom disease list JSON data here..."
                        />

                        <label className="input-label" htmlFor="risk-data">
                            Risk Data
                        </label>
                        <div className="input-with-button">
                            <input
                                readOnly
                                id="risk-data"
                                className="org-input"
                                type="text"
                                value={riskData || ""}
                                placeholder={riskDataPlaceholder}
                            />
                            <button
                                type="button"
                                className="copy-button"
                                onClick={() => copyOutput(riskData, "risk")}
                                disabled={!riskData}
                            >
                                {copiedField === "risk" ? "Copied!" : "Copy"}
                            </button>
                        </div>

                        <label className="input-label" htmlFor="base64-data">
                            Base64 Data
                        </label>
                        <div className="input-with-button">
                            <input
                                readOnly
                                id="base64-data"
                                className="org-input"
                                type="text"
                                value={base64Data || ""}
                                placeholder={base64DataPlaceholder}
                            />
                            <button
                                type="button"
                                className="copy-button"
                                onClick={() => copyOutput(base64Data, "base64")}
                                disabled={!base64Data}
                            >
                                {copiedField === "base64" ? "Copied!" : "Copy"}
                            </button>
                        </div>

                        {errorMessages.length > 0 && (
                            <div className="error-container">
                                {errorMessages.map((error, index) => (
                                    <div key={index}>{error}</div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="drawer-footer">
                        <button
                            className="load-button"
                            onClick={handleManualLoad}
                            disabled={!orgIdentifier.trim() || !jsonContent.trim()}
                        >
                            Load Pedigree
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PedigreeViewerPage;
